import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import LanguageSelector from "../LanguageSelector/LanguageSelector";
import heroBackgroundImage from "../../assets/Program Banner.png";
import carouselBankImg from "../../assets/carousel_bank.png";
import sgpEmblemImg from "../../assets/sgp-emblem.png";
import "./Dashboard.css";

function Dashboard() {
  const navigate = useNavigate();
  const [currentAd, setCurrentAd] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showExploreMenu, setShowExploreMenu] = useState(false);
  const notifRef = useRef(null);
  const exploreRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
      if (exploreRef.current && !exploreRef.current.contains(e.target)) {
        setShowExploreMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const newsItems = [
    "New scholarship initiative for meritorious students",
    "State scholarship portal deadline extended",
    "Check eligibility for various schemes",
    "Central Sector Scheme 2026-27 applications open online",
    "Aadhaar-seed your bank account before applying to avoid DBT payment failure",
    "PMS-OBC and SC/ST application verification window open",
  ];

  const scholarshipAds = [
    {
      id: 1,
      title: "How to activate DBT",
      subtitle: "Ask your bank about Aadhaar seeding and NPCI mapping.",
      theme: "bank",
      badges: [
        { icon: "🎓", text: "All Students", type: "students" },
        { icon: "🏦", text: "Bank Verification Required", type: "verification" },
      ],
      flow: [
        { icon: "🏛️", label: "Visit Bank", tone: "blue" },
        { icon: "📝", label: "Submit Request", tone: "amber" },
        { icon: "✅", label: "Receive Status", tone: "green" },
      ],
    },
    {
      id: 2,
      title: "What is Direct Benefit Transfer (DBT)?",
      subtitle: "Scholarship money transfers directly into students' bank accounts without intermediaries.",
      theme: "transfer",
      badges: [
        { icon: "💰", text: "Scholarship Payments", type: "students" },
        { icon: "⚡", text: "DBT Enabled Account Required", type: "verification" },
      ],
      flow: [
        { icon: "📜", label: "Govt Scheme", tone: "indigo" },
        { icon: "🔄", label: "DBT System", tone: "cyan" },
        { icon: "💳", label: "Student Account", tone: "green" },
      ],
    },
    {
      id: 3,
      title: "Why DBT is Important for Scholarships",
      subtitle: "Prevents delays, eliminates middlemen, and guarantees full credited amount.",
      theme: "speed",
      badges: [
        { icon: "👥", text: "All Students", type: "students" },
        { icon: "🚀", text: "Faster Disbursement", type: "verification" },
      ],
      flow: [
        { icon: "🔗", label: "Aadhaar Link", tone: "cyan" },
        { icon: "🌱", label: "NPCI Seeded", tone: "purple" },
        { icon: "💵", label: "Funds Credited", tone: "green" },
      ],
    },
    {
      id: 4,
      title: "Aadhaar Linked vs. Aadhaar Seeded",
      subtitle: "Linking connects identity, but NPCI seeding is required to receive direct funds.",
      theme: "identity",
      badges: [
        { icon: "⚖️", text: "Essential Distinction", type: "students" },
        { icon: "🔍", text: "NPCI Mapper Check", type: "verification" },
      ],
      flow: [
        { icon: "🆔", label: "Linked Account", tone: "blue" },
        { icon: "🌾", label: "Bank Seeding", tone: "amber" },
        { icon: "🎯", label: "DBT Activated", tone: "green" },
      ],
    },
    {
      id: 5,
      title: "DBT Enabled Aadhaar Seeded Account",
      subtitle: "Successfully mapped on NPCI central database for transparent welfare credits.",
      theme: "ready",
      badges: [
        { icon: "🌟", text: "Fully Ready", type: "students" },
        { icon: "🛡️", text: "Zero Intermediaries", type: "verification" },
      ],
      flow: [
        { icon: "🌐", label: "Central Portal", tone: "indigo" },
        { icon: "⚙️", label: "Core Banking", tone: "blue" },
        { icon: "💸", label: "Instant Credit", tone: "green" },
      ],
    },
    {
      id: 6,
      title: "Why Students Face Payment Delays",
      subtitle: "Incomplete bank seeding or mismatch in name spelling causes payment bounce.",
      theme: "warning",
      badges: [
        { icon: "⚠️", text: "Common Hurdle", type: "students" },
        { icon: "🛠️", text: "Pre-Check Resolves This", type: "verification" },
      ],
      flow: [
        { icon: "🔤", label: "Name Check", tone: "amber" },
        { icon: "👤", label: "Aadhaar Match", tone: "cyan" },
        { icon: "✨", label: "Clean Approval", tone: "green" },
      ],
    },
    {
      id: 7,
      title: "Steps to Complete Bank NPCI Seeding",
      subtitle: "Submit simple consent form to branch manager to designate primary subsidy account.",
      theme: "steps",
      badges: [
        { icon: "📌", text: "One-Time Action", type: "students" },
        { icon: "⏱️", text: "Active in 2-3 Days", type: "verification" },
      ],
      flow: [
        { icon: "📋", label: "Consent Form", tone: "purple" },
        { icon: "📄", label: "Aadhaar Copy", tone: "blue" },
        { icon: "🔁", label: "Mapper Update", tone: "green" },
      ],
    },
    {
      id: 8,
      title: "Check Your DBT Readiness Score",
      subtitle: "Verify IFSC structure, Aadhaar link status and name matching before portal submission.",
      theme: "score",
      badges: [
        { icon: "🧭", text: "Pre-Submission", type: "students" },
        { icon: "📊", text: "Instant Diagnostics", type: "verification" },
      ],
      flow: [
        { icon: "📤", label: "Upload Data", tone: "blue" },
        { icon: "🤖", label: "Auto-Verify", tone: "purple" },
        { icon: "🏆", label: "Readiness Pass", tone: "green" },
      ],
    },
    {
      id: 9,
      title: "Document Consistency Across Schemes",
      subtitle: "Ensure student name, father name, and DOB match exactly across all certificates.",
      theme: "docs",
      badges: [
        { icon: "📑", text: "All Schemes", type: "students" },
        { icon: "💯", text: "Zero Rejection", type: "verification" },
      ],
      flow: [
        { icon: "📖", label: "Marks Card", tone: "amber" },
        { icon: "🪪", label: "Aadhaar Card", tone: "blue" },
        { icon: "🤝", label: "Exact Match", tone: "green" },
      ],
    },
    {
      id: 10,
      title: "Important Scholarship Notice",
      subtitle: "Central and state scholarships credit exclusively to active DBT-mapped accounts.",
      theme: "notice",
      badges: [
        { icon: "📢", text: "Mandatory Guideline", type: "students" },
        { icon: "🔒", text: "Secure Disbursement", type: "verification" },
      ],
      flow: [
        { icon: "🎖️", label: "Scheme Approval", tone: "indigo" },
        { icon: "📲", label: "DBT Transfer", tone: "cyan" },
        { icon: "🎉", label: "Direct Credit", tone: "green" },
      ],
    },
  ];
  // Carousel auto rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentAd((prev) => (prev + 1) % scholarshipAds.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [scholarshipAds.length]);

  const handleNavigation = (path) => {
    setIsExiting(true);
    setTimeout(() => {
      navigate(path);
    }, 250);
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const activeSlide = scholarshipAds[currentAd];

  return (
    <div className={`dashboard-page ${isExiting ? "is-exiting-down" : "is-entering-up"}`}>

      {/* ============================================ SECTION A: HERO CONTAINER (INCLUDES FLOATING NAVBAR) ============================================ */}
      <section className="ref-hero" id="hero-section" style={{ backgroundImage: `url("${heroBackgroundImage}")` }}>

        {/* Floating Capsule Navbar at Top of Hero */}
        <header className="ref-floating-header">
          <div className="ref-brand" onClick={() => scrollToSection("hero-section")}>
            <div className="ref-brand-icon">
              <img src={sgpEmblemImg} alt="SGP Emblem" className="ref-brand-img" />
            </div>
            <div className="ref-brand-titles">
              <span className="ref-brand-abbr">SGP</span>
              <div className="ref-brand-divider"></div>
              <div className="ref-brand-subtitles">
                <span>Scholarship Guidance</span>
                <span>Platform</span>
              </div>
            </div>
          </div>

          <nav className="ref-nav">
            <button
              className="ref-nav-link"
              onClick={() => scrollToSection("why-choose-section")}
              id="nav-about-btn"
            >
              About
            </button>

            <div className="ref-dropdown-wrap" ref={exploreRef}>
              <button
                className="ref-nav-link"
                onClick={() => setShowExploreMenu(!showExploreMenu)}
                id="nav-explore-btn"
              >
                <span>Explore</span> <span className="ref-chevron">▾</span>
              </button>
              {showExploreMenu && (
                <div className="ref-nav-dropdown">
                  <div className="ref-dropdown-item" onClick={() => { setShowExploreMenu(false); handleNavigation("/scholarship"); }}>
                    <span className="dropdown-icon">📖</span>
                    <div>
                      <strong>Scholarship Guide</strong>
                      <p>Browse available central &amp; state schemes</p>
                    </div>
                  </div>
                  <div className="ref-dropdown-item" onClick={() => { setShowExploreMenu(false); handleNavigation("/eligibility"); }}>
                    <span className="dropdown-icon">🔍</span>
                    <div>
                      <strong>Eligibility Engine</strong>
                      <p>Instant rule-based eligibility check</p>
                    </div>
                  </div>
                  <div className="ref-dropdown-item" onClick={() => { setShowExploreMenu(false); handleNavigation("/readiness"); }}>
                    <span className="dropdown-icon">📊</span>
                    <div>
                      <strong>Realtime Status</strong>
                      <p>Check bank &amp; DBT readiness</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="ref-dropdown-wrap" ref={notifRef}>
              <button
                className="ref-nav-link ref-nav-notif"
                onClick={() => setShowNotifications(!showNotifications)}
                id="nav-notif-btn"
              >
                <svg className="ref-nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 7h18s-3 0-3-7" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                Notifications
              </button>

              {showNotifications && (
                <div className="ref-notif-popover">
                  <div className="ref-notif-header">
                    <h4>Pre-Submission Advisories</h4>
                    <span className="ref-notif-count">3 Active</span>
                  </div>
                  <div className="ref-notif-item" onClick={() => { setShowNotifications(false); handleNavigation("/readiness"); }}>
                    <span className="notif-bullet">⚠️</span>
                    <div>
                      <strong>DBT Bank Seeding Mandatory</strong>
                      <p>Confirm account mapping on NPCI before scheme submission.</p>
                    </div>
                  </div>
                  <div className="ref-notif-item" onClick={() => { setShowNotifications(false); handleNavigation("/eligibility"); }}>
                    <span className="notif-bullet">🎯</span>
                    <div>
                      <strong>Central Sector Scheme 2026-27</strong>
                      <p>Verify income ceilings and qualifying score thresholds.</p>
                    </div>
                  </div>
                  <div className="ref-notif-item" onClick={() => { setShowNotifications(false); handleNavigation("/documents"); }}>
                    <span className="notif-bullet">📄</span>
                    <div>
                      <strong>Document Name Matching</strong>
                      <p>Pre-check marksheet and Aadhaar spelling parity.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="ref-lang-container">
              <LanguageSelector />
            </div>
          </nav>
        </header>

        {/* Hero Body Content */}
        <div className="ref-hero-container">
          <div className="ref-hero-content">
            <h1 className="ref-hero-title">
              <span className="ref-hero-title-white">Prepare today.</span>
              <span className="ref-hero-title-yellow">Unlock tomorrow.</span>
            </h1>
            <p className="ref-hero-subtitle">
              Review documents and explore potential<br className="hero-sub-break" />
              scholarship matches.
            </p>
            <div className="ref-hero-actions">
              <button
                className="ref-btn-primary"
                onClick={() => handleNavigation("/documents")}
                id="hero-upload-btn"
              >
                <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <span>Upload Documents</span>
                <span className="btn-arrow">→</span>
              </button>

              <button
                className="ref-btn-glass"
                onClick={() => handleNavigation("/eligibility")}
                id="hero-check-btn"
              >
                <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <span>Check Eligibility</span>
                <span className="btn-arrow">→</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ MAIN CONTENT WRAPPER ============================================ */}
      <main className="ref-main-container">

      <section className="ref-news-strip" id="news-section" aria-label="Latest news">
        <div className="ref-news-inner">
          <span className="ref-news-badge">LATEST NEWS</span>
          <div className="ref-news-track-wrapper">
            <div className="ref-news-track">
              {[0, 1].map((copy) => (
                <span className="ref-news-group" key={copy} aria-hidden={copy === 1 ? true : undefined}>
                  {newsItems.map((item) => (
                    <span className="ref-news-item" key={item}>
                      <span className="ref-news-text">{item}</span>
                      <span className="ref-news-pipe" aria-hidden="true">|</span>
                    </span>
                  ))}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <aside className="ref-alert-strip dashboard-bank-alert" aria-label="Scholarship bank account alert">
        <div className="ref-alert-inner">
          <span className="ref-alert-icon" aria-hidden="true">&#9888;</span>
          <span className="ref-alert-msg"><strong>ALERT:</strong> SCHOLARSHIPS ARE CREDITED ONLY TO DBT-ENABLED, AADHAAR-SEEDED BANK ACCOUNTS. Check Aadhaar seeding and DBT status with your bank.</span>
        </div>
      </aside>

        {/* ============================================ SECTION D: PRIVACY & SECURITY NOTICE ============================================ */}

        {/* ============================================ SECTION E: BLUE DBT INFORMATION CAROUSEL ============================================ */}
        <section className="ref-carousel-section">
          <div className={`ref-carousel-card carousel-theme-${activeSlide.theme}`}>
            {/* Top counter */}
            <div className="ref-carousel-counter">
              {currentAd + 1} / {scholarshipAds.length}
            </div>

            {/* Slide Body */}
            <div className="ref-slide-body">
              {/* Left Column: 3D Bank Building */}
              <div className="ref-slide-bank">
                <img
                  src={carouselBankImg}
                  alt="DBT Bank Illustration"
                  className="ref-bank-illustration"
                />
              </div>

              {/* Middle Column: Title, Subtitle, Badges */}
              <div className="ref-slide-info">
                <h3 className="ref-slide-title">{activeSlide.title}</h3>
                <p className="ref-slide-subtitle">{activeSlide.subtitle}</p>

                <div className="ref-slide-badges">
                  {activeSlide.badges.map((b, idx) => (
                    <span
                      key={idx}
                      className={`ref-slide-badge ${b.type === "verification" ? "badge-verification" : "badge-students"}`}
                    >
                      <span className="badge-ico">{b.icon}</span>
                      <span>{b.text}</span>
                    </span>
                  ))}
                </div>
              </div>

              {/* Right Column: Step Flow */}
              <div className="ref-slide-flow">
                {activeSlide.flow.map((step, idx) => (
                  <React.Fragment key={idx}>
                    <div className="ref-flow-step">
                      <div className={`ref-flow-circle tone-${step.tone || "blue"}`}>{step.icon}</div>
                      <span className="ref-flow-label">{step.label}</span>
                    </div>
                    {idx < activeSlide.flow.length - 1 && (
                      <span className="ref-flow-connector">+</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Navigation Chevrons */}
            <button
              className="ref-carousel-arrow arrow-prev"
              onClick={() => setCurrentAd((prev) => (prev - 1 + scholarshipAds.length) % scholarshipAds.length)}
              aria-label="Previous Slide"
            >
              ‹
            </button>
            <button
              className="ref-carousel-arrow arrow-next"
              onClick={() => setCurrentAd((prev) => (prev + 1) % scholarshipAds.length)}
              aria-label="Next Slide"
            >
              ›
            </button>

            {/* Slide Dots Pagination */}
            <div className="ref-carousel-dots">
              {scholarshipAds.map((_, idx) => (
                <button
                  key={idx}
                  className={`ref-dot ${idx === currentAd ? "active" : ""}`}
                  onClick={() => setCurrentAd(idx)}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </section>

        {/* ============================================ SECTION F: “CHOOSE YOUR NEXT STEP” (6 CARDS, 3 COLUMNS) ============================================ */}
        <section className="ref-steps-section" id="choose-next-step">
          <div className="ref-section-heading">
            <h2>Choose your next step</h2>
            <p>Everything you need to get ready for SGP</p>
          </div>

          <div className="ref-steps-grid">
            {/* ROW 1 - CARD 1: Scholarship Guide */}
            <div
              className="ref-step-card"
              onClick={() => handleNavigation("/scholarship")}
              id="card-scholarship-guide"
            >
              <div className="step-card-top">
                <div className="step-icon-box box-blue">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                  </svg>
                </div>
              </div>
              <div className="step-card-content">
                <h3>Scholarship Guide</h3>
                <p>Learn about available opportunities before you apply.</p>
              </div>
              <div className="step-card-action">
                <div className="action-circle-btn">
                  <span>→</span>
                </div>
              </div>
            </div>

            {/* ROW 1 - CARD 2: Upload Documents */}
            <div
              className="ref-step-card"
              onClick={() => handleNavigation("/documents")}
              id="card-upload-documents"
            >
              <div className="step-card-top">
                <div className="step-icon-box box-green">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                </div>
              </div>
              <div className="step-card-content">
                <h3>Upload Documents</h3>
                <p>Review and upload data from your certificates and identity.</p>
              </div>
              <div className="step-card-action">
                <div className="action-circle-btn">
                  <span>→</span>
                </div>
              </div>
            </div>

            {/* ROW 1 - CARD 3: Check Eligibility */}
            <div
              className="ref-step-card"
              onClick={() => handleNavigation("/eligibility")}
              id="card-check-eligibility"
            >
              <div className="step-card-top">
                <div className="step-icon-box box-purple">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </div>
              </div>
              <div className="step-card-content">
                <h3>Check Eligibility</h3>
                <p>Explore potential scholarship matches instantly.</p>
              </div>
              <div className="step-card-action">
                <div className="action-circle-btn">
                  <span>→</span>
                </div>
              </div>
            </div>

            {/* ROW 2 - CARD 4: Realtime Status */}
            <div
              className="ref-step-card"
              onClick={() => handleNavigation("/readiness")}
              id="card-realtime-status"
            >
              <div className="step-card-top">
                <div className="step-icon-box box-orange">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="20" x2="18" y2="10" />
                    <line x1="12" y1="20" x2="12" y2="4" />
                    <line x1="6" y1="20" x2="6" y2="14" />
                  </svg>
                </div>
              </div>
              <div className="step-card-content">
                <h3>Realtime Status</h3>
                <p>Track your application status and document progress.</p>
              </div>
              <div className="step-card-action">
                <div className="action-circle-btn">
                  <span>→</span>
                </div>
              </div>
            </div>

            {/* ROW 2 - CARD 5: Renewal Reminders */}
            <div
              className="ref-step-card"
              onClick={() => handleNavigation("/renewal")}
              id="card-renewal-reminders"
            >
              <div className="step-card-top">
                <div className="step-icon-box box-red">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </div>
              </div>
              <div className="step-card-content">
                <h3>Renewal Reminders</h3>
                <p>Stay informed about renewal requirements and timelines.</p>
              </div>
              <div className="step-card-action">
                <div className="action-circle-btn">
                  <span>→</span>
                </div>
              </div>
            </div>

            {/* ROW 2 - CARD 6: Help & Feedback */}
            <div
              className="ref-step-card"
              onClick={() => handleNavigation("/reports")}
              id="card-help-feedback"
            >
              <div className="step-card-top">
                <div className="step-icon-box box-teal">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
              </div>
              <div className="step-card-content">
                <h3>Help &amp; Feedback</h3>
                <p>Get answers and share your feedback with us.</p>
              </div>
              <div className="step-card-action">
                <div className="action-circle-btn">
                  <span>→</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================ SECTION G: “WHY CHOOSE OUR PLATFORM?” (3 INFO PANELS) ============================================ */}
        <section className="ref-why-section" id="why-choose-section">
          <div className="ref-section-heading">
            <h2>Why Choose Our Platform?</h2>
          </div>

          <div className="ref-panels-grid">
            {/* PANEL 1: Common application issues (Red) */}
            <div className="ref-info-panel panel-red">
              <div className="panel-header">
                <div className="panel-badge-icon badge-red">
                  <span>!</span>
                </div>
                <div className="panel-header-text">
                  <h3 className="title-red">Common application issues</h3>
                  <p>Know the most frequent issues.</p>
                </div>
              </div>

              <div className="panel-items-list">
                <div className="panel-row-item">
                  <span className="item-icon">📄</span>
                  <span className="item-text">Ineligible applications</span>
                </div>
                <div className="panel-row-item">
                  <span className="item-icon">👤</span>
                  <span className="item-text">Details do not match</span>
                </div>
                <div className="panel-row-item">
                  <span className="item-icon">🏦</span>
                  <span className="item-text">Bank mapping issues</span>
                </div>
                <div className="panel-row-item">
                  <span className="item-icon">⚠️</span>
                  <span className="item-text">Scheme criteria not met</span>
                </div>
              </div>
            </div>

            {/* PANEL 2: How SGP helps (Blue) */}
            <div className="ref-info-panel panel-blue">
              <div className="panel-header">
                <div className="panel-badge-icon badge-blue">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" fill="#22d3ee" stroke="#075bff" /><path d="M12 3v18c4-2 7-6 7-10V6z" fill="#00b7f4" stroke="none" />
                  </svg>
                </div>
                <div className="panel-header-text">
                  <h3 className="title-blue">How SGP helps</h3>
                  <p>Get step-by-step guidance.</p>
                </div>
              </div>

              <div className="panel-items-list">
                <div className="panel-row-item">
                  <span className="item-icon">📄</span>
                  <span className="item-text">Review document criteria</span>
                </div>
                <div className="panel-row-item">
                  <span className="item-icon">🎯</span>
                  <span className="item-text">Check potential eligibility</span>
                </div>
                <div className="panel-row-item">
                  <span className="item-icon task-check" aria-hidden="true">&#10003;</span>
                  <span className="item-text">See application tasks</span>
                </div>
              </div>
            </div>

            {/* PANEL 3: How approvals reach you (Green) */}
            <div className="ref-info-panel panel-green">
              <div className="panel-header">
                <div className="panel-badge-icon badge-green" aria-hidden="true">
                  <svg viewBox="0 0 48 48" fill="none">
                    <g transform="rotate(-38 24 24)" stroke="#00a76f" strokeWidth="2">
                      <rect x="8" y="7" width="30" height="20" rx="3" fill="#13d594" />
                      <path d="M13 12h20v10H13z" stroke="white" />
                      <circle cx="23" cy="17" r="4" fill="#b7ffe3" stroke="white" />
                      <rect x="5" y="23" width="30" height="17" rx="3" fill="#00ba79" />
                      <path d="M17 23v17h7V23" fill="#b4ffe1" stroke="white" />
                    </g>
                  </svg>
                </div>
                <div className="panel-header-text">
                  <h3 className="title-green">How approvals reach you</h3>
                  <p>Scholarships are credited via DBT.</p>
                </div>
              </div>

              {/* 3-Step Flow Diagram */}
              <div className="panel-flow-box">
                <div className="pflow-item">
                  <div className="pflow-icon">🏛️</div>
                  <span className="pflow-text">Government Database</span>
                </div>
                <div className="pflow-arrow">→</div>
                <div className="pflow-item">
                  <div className="pflow-icon">💳</div>
                  <span className="pflow-text">Bank Account</span>
                </div>
                <div className="pflow-arrow">→</div>
                <div className="pflow-item">
                  <div className="pflow-icon">🏛️</div>
                  <span className="pflow-text">Student Receives</span>
                </div>
              </div>

              {/* Checkbox Notice */}
              <div className="panel-check-banner">
                <span className="check-box-icon" aria-hidden="true">&#10003;</span>
                <span className="check-box-text">Check payment status on the official portal.</span>
              </div>
            </div>
          </div>

          {/* ============================================ SECTION H: ABOUT PLATFORM, PRIVACY FIRST, NO LOGIN NEEDED ============================================ */}
          <div className="ref-feature-cards-row" id="about-platform">
            <button
              type="button"
              className="ref-feature-pill-card"
              onClick={() => handleNavigation("/scholarship")}
            >
              <div className="f-pill-left">
                <div className="f-pill-icon icon-blue"><svg viewBox="0 0 48 48" aria-hidden="true"><path d="M5 18 24 9l19 9-19 9z" fill="#0755dc" stroke="#fff" strokeWidth="1.5"/><path d="M12 24v12q12 8 24 0V24l-12 6z" fill="#1263e8"/><path d="M42 20v16" stroke="#0755dc" strokeWidth="3"/></svg></div>
                <div className="f-pill-text">
                  <h4>About Platform</h4>
                  <p>Scholarship program guidance.</p>
                </div>
              </div>
              <div className="f-pill-arrow">→</div>
            </button>

            <button
              type="button"
              className="ref-feature-pill-card"
              onClick={() => handleNavigation("/documents")}
            >
              <div className="f-pill-left">
                <div className="f-pill-icon icon-green"><svg viewBox="0 0 48 48" aria-hidden="true"><path d="M15 23V14a9 9 0 0 1 18 0v9" fill="none" stroke="#00b978" strokeWidth="4"/><rect x="9" y="20" width="30" height="25" rx="5" fill="#00c486"/><circle cx="24" cy="30" r="3" fill="white"/><path d="M24 30v7" stroke="white" strokeWidth="3"/></svg></div>
                <div className="f-pill-text">
                  <h4>Privacy First</h4>
              <p>Document checks run in your browser.</p>
                </div>
              </div>
              <div className="f-pill-arrow">→</div>
            </button>

            <button
              type="button"
              className="ref-feature-pill-card"
              onClick={() => handleNavigation("/eligibility")}
            >
              <div className="f-pill-left">
                <div className="f-pill-icon icon-purple"><svg viewBox="0 0 48 48" aria-hidden="true"><path d="M25 3 11 27h12l-5 19 21-29H27l7-14z" fill="#8200ed" stroke="#ae59ff" strokeWidth="1.5"/></svg></div>
                <div className="f-pill-text">
                  <h4>No Login Needed</h4>
                  <p>Basic tools, limited without signing in.</p>
                </div>
              </div>
              <div className="f-pill-arrow">→</div>
            </button>
          </div>
        </section>

        {/* ============================================ SECTION I: “BEFORE YOU APPLY” BANNER ============================================ */}
        <section className="ref-before-banner">
          <div className="before-banner-inner">
            <div className="before-banner-copy">
              <h3>Before you apply</h3>
              <p>Make sure your documents, eligibility and bank details are ready.</p>
              <p className="before-banner-sub">A quick check now can save time later.</p>
            </div>

            <div className="before-banner-visual">
              <svg className="before-banner-img" viewBox="1000 1120 1040 770" style={{ overflow: "hidden" }} role="img" aria-label="Blue graduation cap and diploma" preserveAspectRatio="xMidYMid slice">
                <image href={heroBackgroundImage} width="3780" height="1890" />
              </svg>
            </div>

            <div className="before-banner-cta">
              <button
                className="ref-btn-yellow"
                onClick={() => handleNavigation("/readiness")}
                id="banner-start-precheck-btn"
              >
                <span>Start Pre-Check</span>
                <span className="btn-arrow">→</span>
              </button>
            </div>
          </div>
        </section>

      </main>

      {/* ============================================ SECTION J: NAVY FOOTER ============================================ */}
      <footer className="ref-footer">
        <div className="ref-footer-inner">
          {/* Col 1: Brand & Social */}
          <div className="ref-footer-col col-brand">
            <div className="footer-brand-title-row">
              <div className="footer-emblem-circle">
                <img src={sgpEmblemImg} alt="SGP Logo" className="footer-emblem-img" />
              </div>
              <div className="footer-brand-name">
                <h3>SGP PORTAL</h3>
                <div className="footer-yellow-line"></div>
              </div>
            </div>
            <p className="footer-brand-desc">
              Your guide to scholarships, applications and important updates from government and state programs.
            </p>
            <div className="footer-social-row">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="social-pill">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14 13.5h2.5l1-4H14v-2c0-1.03 0-2 2-2h1.5V2.14C17.17 2.1 15.95 2 14.66 2 11.98 2 10 3.66 10 6.7v2.8H7v4h3V22h4v-8.5z" />
                </svg>
              </a>
              <a href="https://x.com" target="_blank" rel="noopener noreferrer" aria-label="X Twitter" className="social-pill">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="social-pill">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2c2.7 0 3.05.01 4.12.06 1.06.05 1.79.22 2.43.47.66.26 1.22.6 1.77 1.15.55.55.89 1.11 1.15 1.77.25.64.42 1.37.47 2.43.05 1.07.06 1.42.06 4.12s-.01 3.05-.06 4.12c-.05 1.06-.22 1.79-.47 2.43a4.9 4.9 0 0 1-1.15 1.77 4.9 4.9 0 0 1-1.77 1.15c-.64.25-1.37.42-2.43.47-1.07.05-1.42.06-4.12.06s-3.05-.01-4.12-.06c-1.06-.05-1.79-.22-2.43-.47a4.9 4.9 0 0 1-1.77-1.15 4.9 4.9 0 0 1-1.15-1.77c-.25-.64-.42-1.37-.47-2.43C2.01 15.05 2 14.7 2 12s.01-3.05.06-4.12c.05-1.06.22-1.79.47-2.43.26-.66.6-1.22 1.15-1.77A4.9 4.9 0 0 1 5.45.53C6.09.28 6.82.11 7.88.06 8.95.01 9.3 0 12 0zm0 5a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm0 8.2a3.2 3.2 0 1 1 0-6.4 3.2 3.2 0 0 1 0 6.4zm5.2-8.4a1.2 1.2 0 1 1-2.4 0 1.2 1.2 0 0 1 2.4 0z" />
                </svg>
              </a>
              <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="social-pill">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8zM9.6 15.5V8.5l6.3 3.5-6.3 3.5z" />
                </svg>
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="social-pill">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.03-1.85-3.03-1.85 0-2.14 1.45-2.14 2.94v5.66H9.36V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Col 2: Portal Links */}
          <div className="ref-footer-col">
            <h4 className="footer-col-heading">Portal Links</h4>
            <ul className="footer-links-list">
              <li><button onClick={() => scrollToSection("why-choose-section")}>• About the Platform</button></li>
              <li><button onClick={() => handleNavigation("/scholarship")}>• Scholarship Guide</button></li>
              <li><button onClick={() => handleNavigation("/eligibility")}>• Eligibility Tools</button></li>
              <li><button onClick={() => handleNavigation("/readiness")}>• Track My Status</button></li>
              <li><button onClick={() => handleNavigation("/renewal")}>• Renewal &amp; Timelines</button></li>
              <li><button onClick={() => setShowNotifications(true)}>• Notifications &amp; Alerts</button></li>
              <li><button onClick={() => handleNavigation("/reports")}>• Helpdesk &amp; Support</button></li>
              <li><button onClick={() => scrollToSection("contact-section")}>• Contact Us</button></li>
            </ul>
          </div>

          {/* Col 3: Useful Links */}
          <div className="ref-footer-col">
            <h4 className="footer-col-heading">Useful Links</h4>
            <ul className="footer-links-list">
              <li><a href="https://scholarships.gov.in" target="_blank" rel="noopener noreferrer">• National Scholarship Portal</a></li>
              <li><a href="https://scholarships.gov.in" target="_blank" rel="noopener noreferrer">• State Scholarship Portals</a></li>
              <li><a href="https://dbtbharat.gov.in" target="_blank" rel="noopener noreferrer">• DBT Portal</a></li>
              <li><button onClick={() => handleNavigation("/readiness")}>• Bank Seeding Guidance</button></li>
              <li><a href="https://myaadhaar.uidai.gov.in" target="_blank" rel="noopener noreferrer">• Aadhaar Services</a></li>
              <li><a href="https://www.npci.org.in" target="_blank" rel="noopener noreferrer">• RBI / NPCI Bank Mapper</a></li>
              <li><a href="https://pgportal.gov.in" target="_blank" rel="noopener noreferrer">• RTI / Grievance</a></li>
              <li><button onClick={() => scrollToSection("choose-next-step")}>• Sitemap</button></li>
            </ul>
          </div>

          {/* Col 4: Student Resources */}
          <div className="ref-footer-col">
            <h4 className="footer-col-heading">Student Resources</h4>
            <ul className="footer-links-list">
              <li><button onClick={() => handleNavigation("/scholarship")}>• Guidelines &amp; SOPs</button></li>
              <li><button onClick={() => handleNavigation("/documents")}>• Important Documents</button></li>
              <li><button onClick={() => handleNavigation("/scholarship")}>• Video Tutorials</button></li>
              <li><button onClick={() => scrollToSection("why-choose-section")}>• Common Queries</button></li>
              <li><button onClick={() => handleNavigation("/readiness")}>• Application Checklists</button></li>
              <li><button onClick={() => handleNavigation("/documents")}>• Forms &amp; Templates</button></li>
              <li><button onClick={() => scrollToSection("news-section")}>• Updates &amp; Notices</button></li>
            </ul>
          </div>

          {/* Col 5: Get in touch */}
          <div className="ref-footer-col col-contact" id="contact-section">
            <h4 className="footer-col-heading">Get in touch</h4>
            <p className="footer-contact-desc">
              For support related to scholarships, applications or technical issues, contact us through our official channels.
            </p>
            <div className="footer-contact-details">
              <div className="contact-row">
                <span className="contact-icon">✉️</span>
                <a href="mailto:support@sgpportal.gov.in">support@sgpportal.gov.in</a>
              </div>
              <div className="contact-row">
                <span className="contact-icon">📞</span>
                <a href="tel:18001234567">1800-123-4567</a>
              </div>
              <div className="contact-row">
                <span className="contact-icon">🕒</span>
                <span>Helpdesk Timing: 9 AM - 6 PM (Mon - Sat)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom copyright line */}
        <div className="ref-footer-bottom">
          <div className="ref-footer-bottom-inner">
            <p className="footer-copyright">
              Copyright &copy; 2024 <strong>Scholarship Guidance Platform</strong> (SGP). All rights reserved.
            </p>
            <div className="footer-bottom-links">
              <button onClick={() => scrollToSection("choose-next-step")}>Sitemap</button>
              <span className="footer-bottom-sep">|</span>
              <button onClick={() => handleNavigation("/documents")}>Privacy Policy</button>
              <span className="footer-bottom-sep">|</span>
              <button onClick={() => scrollToSection("why-choose-section")}>Terms of Use</button>
            </div>
          </div>
        </div>
      </footer>


    </div>
  );
}

export default Dashboard;
